// -----------------------------------------------------------
// FloraIQ - PublicationStagePanel (Aba Etapa 7 - Publicação e Gestão)
// -----------------------------------------------------------

import { useState } from "react";
import { publicationChecklist, publicationProducts } from "@/mocks/operational";

const pubStatusColors: Record<string, string> = {
  "Publicado": "bg-green-100 text-green-700",
  "Gerado": "bg-accent-100 text-accent-700",
  "Em revisão": "bg-yellow-100 text-yellow-700",
  "Em geração": "bg-blue-100 text-blue-700",
  "Não gerado": "bg-background-100 text-foreground-500",
  "Atualização pendente": "bg-orange-100 text-orange-700",
};

export default function PublicationStagePanel() {
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(
    Object.fromEntries(publicationChecklist.map((c) => [c.id, c.concluido]))
  );

  const toggleCheck = (id: string) => {
    setCheckedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const completedCount = Object.values(checkedItems).filter(Boolean).length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Checklist */}
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-card border border-background-200/70 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-foreground-900">Checklist de Publicação</h4>
              <span className="text-xs text-foreground-500">{completedCount}/{publicationChecklist.length}</span>
            </div>
            <div className="w-full bg-background-100 rounded-full h-1.5 mb-3">
              <div className="bg-green-500 h-1.5 rounded-full transition-all" style={{ width: `${(completedCount / publicationChecklist.length) * 100}%` }}></div>
            </div>
            <div className="space-y-1">
              {publicationChecklist.map((item) => (
                <button key={item.id} onClick={() => toggleCheck(item.id)} className="w-full flex items-start gap-2.5 p-2.5 rounded-lg text-left transition-colors cursor-pointer hover:bg-background-50/70">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${
                    checkedItems[item.id] ? "bg-green-500 border-green-500 text-white" : "border-background-300 text-transparent hover:border-green-300"
                  }`}>
                    <i className="ri-check-line text-xs"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs ${checkedItems[item.id] ? "text-foreground-500 line-through" : "text-foreground-800"}`}>{item.descricao}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-foreground-400">{item.responsavel}</span>
                      <span className="text-[10px] text-foreground-300">•</span>
                      <span className="text-[10px] text-foreground-400">{item.prazo}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Side */}
        <div className="rounded-card border border-background-200/70 p-4">
          <h4 className="text-sm font-bold text-foreground-900 mb-3">Produtos Publicados</h4>
          <div className="space-y-1.5">
            {publicationProducts.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2 border-b border-background-100/70 last:border-0">
                <div className="min-w-0 mr-2">
                  <p className="text-xs text-foreground-700 truncate">{p.produto}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[10px] text-foreground-400">{p.tipo}</span>
                    <span className={`text-[10px] ${p.visibilidade === "publico" ? "text-green-600" : "text-foreground-400"}`}>
                      {p.visibilidade === "publico" ? "Público" : "Privado"}
                    </span>
                  </div>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${pubStatusColors[p.status] || ""}`}>
                  {p.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Publication products table */}
      <div className="rounded-card border border-background-200/70 p-4">
        <h4 className="text-sm font-bold text-foreground-900 mb-3">Grade de Publicação</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/50">
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Produto</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Tipo</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Status</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Visibilidade</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Última Geração</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Responsável</th>
              </tr>
            </thead>
            <tbody>
              {publicationProducts.map((p) => (
                <tr key={p.id} className="border-b border-background-100/70 hover:bg-background-50/50">
                  <td className="px-2 py-2 text-xs font-medium text-foreground-800">{p.produto}</td>
                  <td className="px-2 py-2 text-xs text-foreground-600">{p.tipo}</td>
                  <td className="px-2 py-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${pubStatusColors[p.status] || ""}`}>{p.status}</span>
                  </td>
                  <td className="px-2 py-2">
                    <span className={`text-[10px] flex items-center gap-1 ${p.visibilidade === "publico" ? "text-green-600" : "text-foreground-500"}`}>
                      <i className={`${p.visibilidade === "publico" ? "ri-earth-line" : "ri-lock-line"} text-xs`}></i>
                      {p.visibilidade === "publico" ? "Público" : "Privado"}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-xs text-foreground-600">{p.ultimaGeracao}</td>
                  <td className="px-2 py-2 text-xs text-foreground-600">{p.responsavel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}